import { Fragment, useMemo, useState, type CSSProperties } from 'react';

import { Select, type SelectOption } from '../components/Select.tsx';
import {
  GroupedCompLabel,
  NestConnector,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../snapshot/components/TruncatedLabel.tsx';
import { EditableLabel } from '../snapshot/views/roadmap/EditableLabel.tsx';

import { orderByParent } from './platform-map-draft.ts';
import styles from './PlatformMapGrid.module.css';
import type { DraftDerivedContext, PlatformMapDraft } from './usePlatformMapDraft.ts';

const LABEL_COLS = 4;
/** Sticky-left offsets for the four label columns (130/42/6/140 + 6px
 *  gaps), matching the Talent / Roadmap freeze geometry. */
const FREEZE_LEFTS = [0, 136, 184, 196] as const;

interface PlatformMapGridProps {
  draft: PlatformMapDraft;
  /** When false the grid is a read-only preview (non-owners). */
  editable: boolean;
  /**
   * When true, cluster each business function's columns under its parent
   * (the "Group functions" nested view). When false, columns render flat
   * in their natural order.
   */
  grouped?: boolean;
}

/**
 * Platform-map editor: derivedContexts (rows, grouped by default — one 40px
 * band each) × business functions (columns). A cell is lit where the
 * derivedContext serves that business function (the adjacency edge).
 *
 * Labels read as clickable fields (hover = highlight only); clicking one
 * opens an inline input with a delete button (see {@link EditableLabel}).
 * The "N skills" toggle expands a derivedContext's skills for editing — and an
 * expanded derivedContext gets a trailing "+ Add New Skill" row. New
 * derivedContexts / business functions are added via the ghost row / column,
 * each minting a row/column in place that opens straight into inline
 * naming; intersections toggle by clicking a cell. Everything mutates the
 * in-memory {@link PlatformMapDraft}; nothing persists until Save & Rerun.
 *
 * Cells are placed by explicit grid coordinates so an expanded
 * derivedContext's intersection cells row-span its skill rows, and its label
 * pins to the top (aligned with the first skill).
 */
export function PlatformMapGrid({ draft, editable, grouped = false }: PlatformMapGridProps) {
  const { framework } = draft;
  const comps = framework.derivedContexts;
  const fns = framework.businessFunctions;
  const F = fns.length;

  // Column order: grouped mode clusters children right after their parent
  // so a group reads as one contiguous band; flat mode keeps natural order.
  const orderedFns = useMemo(() => (grouped ? orderByParent(fns) : fns), [grouped, fns]);

  // Sub-grouping hierarchy (single level): a function can be nested under a
  // top-level parent. `parentSet` = ids that are themselves parents (have
  // children); a parent can't also be nested, keeping the tree one deep.
  const parentSet = useMemo(() => {
    const s = new Set<string>();
    for (const f of fns) if (f.parentFunctionId) s.add(f.parentFunctionId);
    return s;
  }, [fns]);
  const topLevelFns = useMemo(() => fns.filter((f) => f.parentFunctionId == null), [fns]);
  const displayById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of fns) m.set(f.id, f.display);
    return m;
  }, [fns]);
  // Child ids grouped by parent. A parent (group) column carries no
  // intersections of its own — derivedContexts link to the children — so its
  // cells are rendered as a READ-ONLY roll-up of the children's edges rather
  // than a dead empty column.
  const childIdsByParent = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const f of fns) {
      if (f.parentFunctionId == null) continue;
      const arr = m.get(f.parentFunctionId) ?? [];
      arr.push(f.id);
      m.set(f.parentFunctionId, arr);
    }
    return m;
  }, [fns]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Id of a freshly-added derivedContext / function / skill — its label opens
  // straight into inline edit so the user names it without a second click.
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Lay derivedContexts out into row blocks. A collapsed derivedContext is one
  // row; an expanded one spans a row per skill (the label pins to the
  // top), matching the flat grouped-skill layout on the other pages. When
  // editable, an expanded derivedContext reserves one extra row for the
  // trailing "+ Add New Skill" affordance.
  const { blocks, ghostRow } = useMemo(() => {
    let row = 2; // row 1 = column headers
    const out = comps.map((comp) => {
      const n = comp.skills.length;
      const isExpanded = expanded.has(comp.id) && n > 0;
      const addRow = isExpanded && editable;
      const span = isExpanded ? n + (addRow ? 1 : 0) : 1;
      const block = { comp, r0: row, span, expanded: isExpanded, n, addRow };
      row += span;
      return block;
    });
    return { blocks: out, ghostRow: row };
  }, [comps, expanded, editable]);

  const ghostCol = LABEL_COLS + F + 1; // 1-based column for the "+ Function" ghost
  const gridStyle: CSSProperties = {
    gridTemplateColumns: `130px 42px 6px 140px repeat(${F}, minmax(72px, 1fr))${
      editable ? ' 110px' : ''
    }`,
    gridAutoRows: 'minmax(40px, auto)',
    paddingLeft: 0,
  };

  return (
    <div className="snap-grid" style={gridStyle}>
      {/* Opaque backing behind the frozen label columns (cols 1–4, all
          rows) so a row-spanning data cell can't peek through the gaps
          between the per-row frozen label cells when scrolled. */}
      <div
        className={styles.frozenBacking}
        style={{ gridRow: `1 / ${ghostRow}`, gridColumn: '1 / span 4' }}
        aria-hidden="true"
      />

      {/* Frozen top-left corner — pins on both axes over the label
          columns where they cross the header row. */}
      <div
        className="snap-grid__freeze-corner is-frozen is-frozen-edge"
        style={{ gridRow: 1, gridColumn: '1 / span 4', left: 0 }}
        aria-hidden="true"
      />

      {/* Header row: business-function column headers (cols 5…4+F). Each
          is a `.snap-grid__col-header` grid item so it pins sticky-top. */}
      {orderedFns.map((fn, i) => {
        const headerStyle: CSSProperties = { gridRow: 1, gridColumn: LABEL_COLS + 1 + i };
        const parentDisplay =
          fn.parentFunctionId != null ? displayById.get(fn.parentFunctionId) ?? null : null;
        // A function that has children stays a top-level group; only leaf
        // functions can be nested. Parent options are the other top-level
        // functions.
        const isParent = parentSet.has(fn.id);
        // In grouped mode, tint a nested child's column so a group reads as
        // a contiguous band under its parent.
        const childInGroup = grouped && parentDisplay != null;
        const parentOptions: SelectOption[] = topLevelFns
          .filter((p) => p.id !== fn.id)
          .map((p) => ({ value: p.id, label: p.display }));
        return editable ? (
          <ColumnHeaderEditable
            key={fn.id}
            style={headerStyle}
            display={fn.display}
            subtitle={fn.subtitle}
            onRename={(next) => draft.renameBusinessFunction(fn.id, next)}
            onEditDescription={(next) => draft.editBusinessFunctionDescription(fn.id, next)}
            onClearDescription={() => draft.editBusinessFunctionDescription(fn.id, '')}
            onDelete={() => draft.deleteBusinessFunction(fn.id)}
            isNew={fn.isNew}
            autoEdit={fn.id === pendingEditId}
            parentId={fn.parentFunctionId}
            parentOptions={parentOptions}
            isParent={isParent}
            onSetParent={(pid) => draft.setBusinessFunctionParent(fn.id, pid)}
            grouped={grouped}
            parentDisplay={parentDisplay}
            childInGroup={childInGroup}
          />
        ) : (
          <div
            key={fn.id}
            className={`snap-grid__col-header${childInGroup ? ` ${styles.groupedChild}` : ''}`}
            style={headerStyle}
          >
            <strong>
              <TruncatedLabel text={fn.display} noReveal />
            </strong>
            {fn.subtitle ? (
              <span className="snap-grid__col-header__subtitle" title={fn.subtitle}>
                {fn.subtitle}
              </span>
            ) : null}
            {parentDisplay ? (
              <span className={styles.parentTag} title={`Grouped under ${parentDisplay}`}>
                ↳ {parentDisplay}
              </span>
            ) : null}
          </div>
        );
      })}

      {/* Empty sticky header cell over the "+ Function" ghost column so it
          scrolls under the header band like the rest of the grid. */}
      {editable ? (
        <div
          className="snap-grid__col-header"
          style={{ gridRow: 1, gridColumn: ghostCol }}
          aria-hidden="true"
        />
      ) : null}

      {blocks.map(({ comp, r0, span, expanded: isExpanded, n, addRow }) => {
        const propCount = comp.skills.filter((s) => s.kind === 'institutional').length;
        const nameNode = editable ? (
          <EditableLabel
            text={comp.display}
            isOverlay={comp.isNew}
            autoEdit={comp.id === pendingEditId}
            onSave={(next) => draft.renameDerivedContext(comp.id, next)}
            onDelete={() => draft.deleteDerivedContext(comp.id)}
          />
        ) : (
          <TruncatedLabel text={comp.display} isNew={comp.isNew} noReveal />
        );
        const cells = orderedFns.map((fn, i) => {
          // Parent columns show a read-only roll-up of their children's edges
          // (lit when any child is linked — a 1-child parent mirrors that
          // child) so a group never reads as an empty column. Editing stays on
          // the children.
          const childIds = childIdsByParent.get(fn.id);
          const isParentCol = childIds != null && childIds.length > 0;
          const lit = isParentCol
            ? childIds.some((cid) => comp.functions.includes(cid))
            : comp.functions.includes(fn.id);
          return (
            <IntersectionCell
              key={fn.id}
              lit={lit}
              editable={editable && !isParentCol}
              preview={isParentCol}
              merged={span > 1}
              label={
                isParentCol
                  ? `${comp.display} × ${fn.display} — rolled up from ${childIds.length} function${childIds.length === 1 ? '' : 's'}`
                  : `${comp.display} × ${fn.display}`
              }
              onToggle={() => draft.toggleIntersection(comp.id, fn.id)}
              style={{ gridRow: `${r0} / ${r0 + span}`, gridColumn: LABEL_COLS + 1 + i }}
            />
          );
        });

        // Collapsed: the shared grouped-derivedContext band (name · connector ·
        // prop · "N skills"), matching the other matrix pages. The
        // connector toggles the skills open.
        if (!isExpanded) {
          return (
            <Fragment key={comp.id}>
              <GroupedCompLabel
                gridRow={r0}
                name={nameNode}
                count={`${n} skill${n === 1 ? '' : 's'}`}
                propCount={propCount}
                expanded={false}
                onToggle={n > 0 ? () => toggleExpand(comp.id) : undefined}
                frozen
                freezeLefts={FREEZE_LEFTS}
              />
              {cells}
            </Fragment>
          );
        }

        // Expanded: the derivedContext name pins to the top (col 1, row-spanning)
        // and the skills list out (cols 2–4); every connector collapses the
        // group back. The tree connector spans the skills plus the trailing
        // "+ Add New Skill" row, so its count is `n + (addRow ? 1 : 0)` —
        // that way the last *skill* draws a mid-segment and the add row owns
        // the bottom elbow.
        const treeCount = n + (addRow ? 1 : 0);
        return (
          <Fragment key={comp.id}>
            <div
              className="snap-grid__row-label snap-grid__row-label--muted snap-grid__row-label--wrap-2 is-frozen"
              style={{ gridRow: `${r0} / ${r0 + span}`, gridColumn: 1, left: FREEZE_LEFTS[0] }}
            >
              {nameNode}
            </div>
            {comp.skills.map((skill, i) => (
              <Fragment key={skill.id}>
                <div
                  className="snap-grid__row-label snap-grid__row-label--conn snap-grid__row-label--conn-toggle is-frozen"
                  role="button"
                  tabIndex={0}
                  aria-expanded
                  aria-label="Group skills"
                  onClick={() => toggleExpand(comp.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(comp.id);
                    }
                  }}
                  style={{ gridRow: r0 + i, gridColumn: 2, left: FREEZE_LEFTS[1] }}
                >
                  <NestConnector variant={nestVariant(i, treeCount)} />
                </div>
                <div
                  className="snap-grid__row-label snap-grid__row-label--prop is-frozen"
                  style={{ gridRow: r0 + i, gridColumn: 3, left: FREEZE_LEFTS[2] }}
                >
                  <PropIndicator institutional={skill.kind === 'institutional'} />
                </div>
                <div
                  className="snap-grid__row-label is-frozen is-frozen-edge"
                  style={{ gridRow: r0 + i, gridColumn: 4, left: FREEZE_LEFTS[3] }}
                >
                  {editable ? (
                    <EditableLabel
                      text={skill.display}
                      isOverlay={skill.isNew}
                      autoEdit={skill.id === pendingEditId}
                      onSave={(next) => draft.renameSkill(comp.id, skill.id, next)}
                      onDelete={() => draft.deleteSkill(comp.id, skill.id)}
                    />
                  ) : (
                    <TruncatedLabel text={skill.display} isNew={skill.isNew} noReveal />
                  )}
                </div>
              </Fragment>
            ))}
            {addRow ? (
              <Fragment>
                <div
                  className="snap-grid__row-label snap-grid__row-label--conn is-frozen"
                  style={{ gridRow: r0 + n, gridColumn: 2, left: FREEZE_LEFTS[1] }}
                  aria-hidden="true"
                >
                  <NestConnector variant={nestVariant(n, treeCount)} />
                </div>
                <div
                  className="snap-grid__row-label snap-grid__row-label--prop is-frozen"
                  style={{ gridRow: r0 + n, gridColumn: 3, left: FREEZE_LEFTS[2] }}
                />
                <div
                  className="snap-grid__row-label is-frozen is-frozen-edge"
                  style={{ gridRow: r0 + n, gridColumn: 4, left: FREEZE_LEFTS[3] }}
                >
                  <button
                    type="button"
                    className={styles.addSkill}
                    onClick={() => setPendingEditId(draft.addSkill(comp.id, 'New skill'))}
                  >
                    + Add New Skill
                  </button>
                </div>
              </Fragment>
            ) : null}
            {cells}
          </Fragment>
        );
      })}

      {/* Ghost affordances (owners only). Each mints a row / column in
          place with a default name and opens it straight into inline edit
          (see `pendingEditId`) — no intermediate picker form. */}
      {editable ? (
        <>
          <button
            type="button"
            className="snap-grid__ghost-column"
            style={{ gridRow: `2 / ${ghostRow}`, gridColumn: ghostCol }}
            onClick={() => setPendingEditId(draft.addBusinessFunction('New function'))}
            aria-label="Add business function"
          >
            <span className="snap-grid__ghost-label">+ Function</span>
          </button>

          <button
            type="button"
            className="snap-grid__ghost-row"
            style={{ gridRow: ghostRow, gridColumn: `${LABEL_COLS + 1} / ${ghostCol}` }}
            onClick={() => setPendingEditId(draft.addDerivedContext('New context'))}
          >
            <span className="snap-grid__ghost-label">+ New Context</span>
          </button>
        </>
      ) : null}
    </div>
  );
}

function IntersectionCell({
  lit,
  editable,
  merged,
  label,
  onToggle,
  style,
  preview = false,
}: {
  lit: boolean;
  editable: boolean;
  merged: boolean;
  label: string;
  onToggle: () => void;
  style: CSSProperties;
  /** Read-only rolled-up parent cell — styled distinctly so it doesn't read
   *  as a directly-editable intersection. */
  preview?: boolean;
}) {
  const className = `snap-cell ${lit ? 'lvl-2' : 'na'}${merged ? ' is-merged' : ''}${
    preview ? ` ${styles.previewCell}` : ''
  }`;
  if (!editable) {
    return (
      <div
        className={className}
        style={style}
        aria-label={`${label}: ${lit ? 'mapped' : 'not mapped'}`}
      />
    );
  }
  return (
    <button
      type="button"
      className={`${className} ${styles.cellButton}`}
      style={style}
      onClick={onToggle}
      aria-pressed={lit}
      aria-label={`${label}: ${lit ? 'mapped — click to remove' : 'not mapped — click to add'}`}
    />
  );
}

/** Editable column header — business-function name (click to rename /
 *  delete) over a click-to-edit description line. */
function ColumnHeaderEditable({
  display,
  subtitle,
  onRename,
  onEditDescription,
  onClearDescription,
  onDelete,
  isNew,
  autoEdit,
  style,
  parentId,
  parentOptions,
  isParent,
  onSetParent,
  grouped,
  parentDisplay,
  childInGroup,
}: {
  display: string;
  subtitle: string | null;
  onRename: (next: string) => void;
  onEditDescription: (next: string) => void;
  onClearDescription: () => void;
  onDelete: () => void;
  isNew?: boolean;
  autoEdit?: boolean;
  style?: CSSProperties;
  /** Current parent function id, or null for top-level. */
  parentId: string | null;
  /** Selectable parent functions (the other top-level functions). */
  parentOptions: SelectOption[];
  /** True when this function already has children (a group header). */
  isParent: boolean;
  onSetParent: (parentId: string | null) => void;
  /** Grouped ("nested") view active — show the group relationship. */
  grouped: boolean;
  /** Display name of this function's parent, when nested. */
  parentDisplay: string | null;
  /** True when this is a nested child rendered in grouped mode. */
  childInGroup: boolean;
}) {
  return (
    <div
      className={`snap-grid__col-header ${styles.colHeader}${childInGroup ? ` ${styles.groupedChild}` : ''}`}
      style={style}
    >
      <strong>
        <EditableLabel
          text={display}
          isOverlay={isNew}
          autoEdit={autoEdit}
          onSave={onRename}
          onDelete={onDelete}
        />
      </strong>
      <span className={styles.colHeaderDesc}>
        <EditableLabel
          text={subtitle ?? ''}
          placeholder="Add description"
          onSave={onEditDescription}
          onDelete={subtitle ? onClearDescription : undefined}
        />
      </span>
      {isParent ? (
        // A function with children stays top-level; nesting it would make a
        // two-level tree. Show a muted marker instead of the picker.
        <span className={styles.parentTag}>Parent group</span>
      ) : grouped && parentDisplay ? (
        // In the nested view, show the group relationship instead of the
        // picker (toggle off to re-parent).
        <span className={styles.parentTag} title={`Grouped under ${parentDisplay}`}>
          ↳ {parentDisplay}
        </span>
      ) : parentOptions.length > 0 ? (
        <Select
          className={styles.parentSelect}
          label="Parent function"
          value={parentId ?? ''}
          onChange={(e) => onSetParent(e.target.value || null)}
          options={[{ value: '', label: 'No parent' }, ...parentOptions]}
        />
      ) : null}
    </div>
  );
}

// Re-exported for any caller that wants the derivedContext draft shape.
export type { DraftDerivedContext };
